import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { 
  User, 
  Users,
  Clock, 
  Star, 
  FolderOpen, 
  Plus, 
  MoreHorizontal, 
  LayoutDashboard,
  Calendar,
  Edit,
  Trash2,
  X,
  Check
} from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setCurrentProject, fetchProjects, updateProject, deleteProject } from '@/store/projectSlice';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';

const SIDEBAR_DEFAULT_WIDTH = 256;
const SIDEBAR_MIN_WIDTH = 220;
const getMaxSidebarWidth = () => {
  if (typeof window === 'undefined') return SIDEBAR_DEFAULT_WIDTH * 2;
  return Math.max(SIDEBAR_MIN_WIDTH, Math.floor(window.innerWidth / 2));
};

const clampSidebarWidth = (value: number) => {
  const maxWidth = getMaxSidebarWidth();
  return Math.min(Math.max(value, SIDEBAR_MIN_WIDTH), maxWidth);
};

export const Sidebar = () => {
  const collapsed = useAppSelector((s) => (s as any).ui?.sidebarCollapsed) as boolean;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { projects, currentProject } = useAppSelector((state) => state.projects);
  const { isAdmin,user } = useAppSelector((state) => state.auth);
  const location = useLocation();
  
  // State for project menu and editing
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const menuRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const editInputRef = useRef<HTMLInputElement>(null);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const resizeStateRef = useRef({ startX: 0, startWidth: SIDEBAR_DEFAULT_WIDTH });
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    if (typeof window === 'undefined') return SIDEBAR_DEFAULT_WIDTH;
    const stored = window.localStorage.getItem('sidebarWidth');
    const parsed = stored ? parseInt(stored, 10) : NaN;
    const initial = Number.isFinite(parsed) ? parsed : SIDEBAR_DEFAULT_WIDTH;
    return clampSidebarWidth(initial);
  });
  const [isResizing, setIsResizing] = useState(false);
  
  // Derive project from URL if currentProject not set
  const projectIdFromUrl = (() => {
    const m = location.pathname.match(/\/projects\/([^/]+)/);
    return m ? m[1] : undefined;
  })();
  const selectedProject = currentProject || projects.find((p) => p.id === projectIdFromUrl);

  // Sort projects by most recently updated/created for "Recent" section
  const sortedProjects = [...projects].sort((a: any, b: any) => {
    const aTime = a.updated_at || a.created_at;
    const bTime = b.updated_at || b.created_at;
    if (!aTime && !bTime) return 0;
    if (!aTime) return 1;
    if (!bTime) return -1;
    return new Date(bTime).getTime() - new Date(aTime).getTime();
  });

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors
     ${isActive ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-100'}`;

  // Route-based active helpers for full-row highlight wrappers
  const isReportsActive = location.pathname.startsWith('/reports');
  const isTasksActive = location.pathname.startsWith('/tasks');
  const isProjectsActive = location.pathname.startsWith('/projects');
  const isTeamsActive = location.pathname.startsWith('/teams');

  // Fetch projects on component mount - only active projects
  useEffect(() => {
    if (projects.length === 0) {
      dispatch(fetchProjects({ is_active: true,organization_id:user?.organization_id }));
    }
  }, [dispatch, projects.length]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      let clickedInsideMenu = false;
      menuRefs.current.forEach((menuElement) => {
        if (menuElement && menuElement.contains(target)) {
          clickedInsideMenu = true;
        }
      });
      if (!clickedInsideMenu) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Persist sidebar width
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('sidebarWidth', String(Math.round(clampSidebarWidth(sidebarWidth))));
  }, [sidebarWidth]);

  // Clamp width on viewport resize
  useEffect(() => {
    const handleResize = () => {
      setSidebarWidth((prev) => clampSidebarWidth(prev));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle dragging behaviour
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (event: MouseEvent) => {
      const delta = event.clientX - resizeStateRef.current.startX;
      const nextWidth = clampSidebarWidth(resizeStateRef.current.startWidth + delta);
      setSidebarWidth(nextWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.classList.remove('select-none');
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  useEffect(() => {
    return () => {
      document.body.classList.remove('select-none');
    };
  }, []);

  const handleResizeStart = (event: React.MouseEvent) => {
    if (collapsed) return;
    if (!sidebarRef.current) return;
    setIsResizing(true);
    resizeStateRef.current = {
      startX: event.clientX,
      startWidth: sidebarRef.current.getBoundingClientRect().width,
    };
    document.body.classList.add('select-none');
    event.preventDefault();
  };

  // Focus input when editing starts
  useEffect(() => {
    if (editingProjectId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingProjectId]);

  // Handle edit project name
  const handleStartEdit = (project: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuId(null);
    setEditingProjectId(project.id);
    setEditName(project.name);
  };

  const handleCancelEdit = () => {
    setEditingProjectId(null);
    setEditName('');
  };

  const handleSaveEdit = async (projectId: string) => {
    if (!editName.trim() || editName.trim().length < 3) {
      return;
    }
    
    setIsUpdating(true);
    try {
      await dispatch(updateProject({ 
        id: projectId, 
        data: { name: editName.trim() } 
      })).unwrap();
      setEditingProjectId(null);
      setEditName('');
      // Refresh projects list - only active projects
      dispatch(fetchProjects({ is_active: true,organization_id:user?.organization_id }));
    } catch (error) {
      // Error toast is handled in the thunk
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle delete project
  const handleDeleteClick = (project: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuId(null);
    setShowDeleteConfirm(project.id);
  };

  const handleConfirmDelete = async () => {
    if (!showDeleteConfirm) return;
    
    setIsDeleting(true);
    try {
      await dispatch(deleteProject(showDeleteConfirm)).unwrap();
      // If deleted project was current, navigate away
      if (currentProject?.id === showDeleteConfirm) {
        dispatch(setCurrentProject(null));
        navigate('/projects');
      }
      // Refresh projects list - only active projects
      dispatch(fetchProjects({ is_active: true,organization_id:user?.organization_id }));
    } catch (error) {
      // Error toast is handled in the thunk
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(null);
    }
  };

  return (
    <aside
      ref={sidebarRef}
      className={`relative h-screen border-r border-gray-200 bg-white sticky top-0 ${collapsed ? 'hidden' : ''} transition-[width] duration-200 z-30`}
      style={!collapsed ? { width: sidebarWidth } : undefined}
    >
      {/* <div className="flex items-center justify-between h-16 px-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary-600 rounded flex items-center justify-center">
            <span className="text-white font-bold text-sm">WN</span>
          </div>
          {!collapsed && <span className="text-lg font-bold text-gray-900">WorkNex</span>}
        </div>
        <button
          className="text-gray-500 hover:text-gray-700"
          onClick={() => setCollapsed((v) => !v)}
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
        </button>
      </div> */}

      <nav className="px-2 py-2 space-y-1">
        {/* For you */}
        <NavLink to="/for-you" className={linkClass}>
          <User className="w-5 h-5" />
          {!collapsed && <span>For you</span>}
        </NavLink>

        {/* Reports */}
        <div
          className={`flex items-center justify-between rounded-md ${
            isReportsActive ? 'bg-primary-50' : ''
          }`}
        >
          <NavLink to="/reports" className={({ isActive }) => `${linkClass({ isActive })} flex-1`}>
            <Clock className="w-5 h-5" />
            {!collapsed && <span>Reports</span>}
          </NavLink>
        </div>

        {/* Tasks */}
        <div
          className={`flex items-center justify-between rounded-md ${
            isTasksActive ? 'bg-primary-50' : ''
          }`}
        >
          <NavLink to="/tasks" className={({ isActive }) => `${linkClass({ isActive })} flex-1`}>
            <Star className="w-5 h-5" />
            {!collapsed && <span>Tasks</span>}
          </NavLink>
        </div>

        {/* Projects */}
        <div
          className={`flex items-center justify-between rounded-md ${
            isProjectsActive ? 'bg-primary-50' : ''
          }`}
        >
          <NavLink to="/projects" className={linkClass}>
            <FolderOpen className="w-5 h-5" />
            {!collapsed && (<span>Projects</span>)}
          </NavLink>
          {!collapsed && (
            <div className="flex items-center gap-1">
              {isAdmin && (
                <button
                  className="p-1 hover:bg-gray-100 rounded"
                  onClick={() => navigate('/projects?create=1')}
                  title="Create project"
                >
                  <Plus className="w-4 h-4 text-gray-500" />
                </button>
              )}
              <button className="p-1 hover:bg-gray-100 rounded" onClick={() => navigate('/projects')} title="More">
                <MoreHorizontal className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          )}
        </div>

        {/* Recent Projects under Projects header */}
        {!collapsed && (
          <div className="ml-6 space-y-1">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide px-3 py-1">
              Recent
            </div>
            {sortedProjects.slice(0, 3).map((project) => {
              const isActive = selectedProject?.id === project.id;
              const isEditing = editingProjectId === project.id;
              const isMenuOpen = openMenuId === project.id;
              
              return (
                <div 
                  key={project.id}
                  className={`flex items-center justify-between rounded-md ${
                    isEditing ? '' : 'cursor-pointer'
                  } ${
                    isActive ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    if (!isEditing) {
                      dispatch(setCurrentProject(project));
                      navigate(`/projects/${project.id}/board`);
                    }
                  }}
                >
                  <div className="flex items-center gap-3 px-3 py-2 flex-1 min-w-0">
                    <div className="w-6 h-6 bg-blue-500 rounded flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">
                        {project.key?.charAt(0) || project.name.charAt(0)}
                      </span>
                    </div>
                    {isEditing ? (
                      <div className="flex items-center gap-1.5 flex-1 min-w-0 pr-2">
                        <input
                          ref={editInputRef}
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveEdit(project.id);
                            } else if (e.key === 'Escape') {
                              handleCancelEdit();
                            }
                          }}
                          className="flex-1 min-w-0 text-sm font-medium px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={isUpdating}
                          onClick={(e) => e.stopPropagation()}
                          maxLength={200}
                        />
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveEdit(project.id);
                            }}
                            disabled={isUpdating || !editName.trim() || editName.trim().length < 3}
                            className="p-1.5 hover:bg-green-100 rounded text-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title="Save"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelEdit();
                            }}
                            disabled={isUpdating}
                            className="p-1.5 hover:bg-red-100 rounded text-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title="Cancel"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <span className={`text-sm font-medium truncate flex-1 min-w-0 ${
                        isActive ? 'text-blue-900' : 'text-gray-900'
                      }`}>
                        {project.name.length > 20 ? `${project.name.substring(0, 20)}...` : project.name}
                      </span>
                    )}
                  </div>
                  {!isEditing && isAdmin && (
                    <div 
                      className="relative mr-2" 
                      ref={(el) => {
                        if (el) {
                          menuRefs.current.set(project.id, el);
                        } else {
                          menuRefs.current.delete(project.id);
                        }
                      }}
                    >
                      <button 
                        className="p-1 hover:bg-gray-100 rounded" 
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          setOpenMenuId(isMenuOpen ? null : project.id);
                        }}
                        title="Project options"
                      >
                        <MoreHorizontal className={`w-4 h-4 ${isActive ? 'text-blue-600' : 'text-gray-500'}`} />
                      </button>
                      {isMenuOpen && (
                        <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                          <button
                            onClick={(e) => handleStartEdit(project, e)}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={(e) => handleDeleteClick(project, e)}
                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {projects.length > 3 && (
              <div className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-md cursor-pointer" onClick={() => navigate('/projects')}>
                <MoreHorizontal className="w-5 h-5" />
                <span>More projects</span>
                {/* <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" /> */}
              </div>
            )}
          </div>
        )}

        {/* Project navigation shortcuts when a project is selected */}
        {selectedProject && (
          <div className="ml-6 space-y-1">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide px-3 py-1">
              Project Navigation
            </div>
            <NavLink to={`/projects/${selectedProject.id}/sprints`} className={linkClass}>
              <Calendar className="w-5 h-5" />
              {!collapsed && <span>Sprints</span>}
            </NavLink>
          </div>
        )}

        {/* Teams */}
        <div
          className={`flex items-center justify-between rounded-md ${isTeamsActive ? 'bg-primary-50' : ''
            } ${!isAdmin ? 'cursor-default' : ''}`}
        >
          <NavLink
            to={isAdmin ? "/teams" : "#"}
            className={({ isActive }) =>
              isAdmin
                ? linkClass({ isActive })
                : `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium
                   text-gray-400 cursor-not-allowed bg-transparent`
            }
            onClick={(e) => {
              if (!isAdmin) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            <Users className="w-5 h-5" />
            {!collapsed && <span>Teams</span>}
          </NavLink>
          {!collapsed && isAdmin && (
            <div className="flex items-center gap-1">
              <button
                className="p-1 hover:bg-gray-100 rounded"
                onClick={() => navigate('/teams?create=1')}
                title="Create team"
              >
                <Plus className="w-4 h-4 text-gray-500" />
              </button>
              <button className="p-1 hover:bg-gray-100 rounded" onClick={() => navigate('/teams')} title="More">
                <MoreHorizontal className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          )}
        </div>


        {/* Filters */}
        {/* <div className={linkClass({ isActive: false })}>
          <Filter className="w-5 h-5" />
          {!collapsed && <span>Filters</span>}
        </div> */}

        {/* Dashboards */}
        <NavLink to="/dashboard" className={linkClass}>
          <LayoutDashboard className="w-5 h-5" />
          {!collapsed && <span>Dashboards</span>}
        </NavLink>

        {/* Customize sidebar */}
        {/* <NavLink to="/settings" className={linkClass}>
          <Settings className="w-5 h-5" />
          {!collapsed && <span>Customize sidebar</span>}
        </NavLink> */}
      </nav>
      
      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <DeleteConfirmDialog
          isOpen={!!showDeleteConfirm}
          itemName={projects.find(p => p.id === showDeleteConfirm)?.name || 'this project'}
          itemType="project"
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteConfirm(null)}
          isLoading={isDeleting}
          customMessage="Are you sure you want to delete this project? This will hide the project and all its tasks, stories, epics, and issues from the system. This action cannot be undone."
        />
      )}

      {!collapsed && (
        <div
          className={`absolute top-0 right-0 h-full w-1 cursor-col-resize ${isResizing ? 'bg-primary-200' : 'bg-transparent hover:bg-primary-100'}`}
          onMouseDown={handleResizeStart}
          aria-hidden="true"
        />
      )}
    </aside>
  );
};
